import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';

const baseQuery = fetchBaseQuery({
    baseUrl: process.env.REACT_APP_BASE_URL,
    prepareHeaders: (headers) => {
        // Prevent caching
        headers.set('Cache-Control', 'no-store, no-cache, must-revalidate, proxy-revalidate');
        headers.set('Pragma', 'no-cache');
        headers.set('Expires', '0');
        headers.set('Surrogate-Control', 'no-store');

        return headers;
    },
    credentials: 'include',
});

export const api = createApi({
    baseQuery,
    reducerPath: 'api',
    tagTypes: [
        //general
        'Login',
        'Signup',
        'Refresh',
        'Logout',
        'UpdateUser',
        'ChnagePassword',

        //user
        'User',
        'AllUsers',
        'SendInvitation',
        'AddUser',
        'ActivateUser',
        'DeactivateUser',
        'RemoveUser',
        'EditUser',

        //devices
        'Devices',
        'DeviceNames',
        'AddDevice',
        'RemoveDevice',
        'Downtime',

        //sites
        'Sites',
        'AllSites',
        'AddSite',
        'RemoveSite',
        'UpdateSite',

        //need to remove
        'AddDeviceToSite',
        'RemoveDeviceFromSite',

        'DisableSite',
        'EnableSite',

        //logics
        'Logics',

        //data
        'Data',
        'OverviewData',
        'HourlyData',
        'DailyData',
        'WeeklyData',
        'MonthlyData',
        'YearlyData',
        'TotalCount',

        //adult&child data
        'AdultnChildOverviewData',
        'AdultnChildHourlyData',
        'AdultnChildDailyData',
        'AdultnChildWeeklyData',
        'AdultnChildMonthlyData',
        'AdultnChildYearlyData',

        //genderData
        'GenderOverviewData',
        'GenderHourlyData',
        'GenderDailyData',
        'GenderWeeklyData',
        'GenderMonthlyData',
        'GenderYearlyData',

        //notifications
        'Notifications',

        //reports
        'ExtremeData',
        'MonthlyReportData',
        'AdultnChildExtremeData',
        'Adult&ChildMonthlyReportData',
        'UpdateSheet',
        'AdultnChildUpdateSheet',
        'SendReportEmail',

        //companies
        'Companies',
        'TotalCompanies',
        'AddCompanies',
        'UpdateCompany',
        'Company',
        'AssignCompany',
        'RemoveCompany',
        'UserLimit',

        //alternatives
        'Alternatives',
        'UpdateAlternative',
        'UpdateSiteAlternative',

        //permissions
        'Permissions',

        //roles
        'AddPermissionToUser',
        'RemovePermissionFromUser',

        //licenses
        'Licenses',
        'AddLisences',

        //packages
        'Package',
        'AddPackage',

        //addon
        'Addon',
        'AddonByDevice',
        'UpdateAddon',

        //group
        'Group',
        'AddGroup',
        'DeleteGroup',
        'UpdateGroup',

        //global
        'Global',
        'FlipDowntime',
    ],

    endpoints: (build) => ({
        //general
        login: build.mutation({
            query: (data) => ({
                url: 'general/login',
                method: 'POST',
                body: data,
            }),
            providesTags: ['Login'],
        }),
        signup: build.mutation({
            query: (userData) => ({
                url: 'general/signUp',
                method: 'POST',
                body: userData,
            }),
            providesTags: ['Signup'],
        }),
        logout: build.mutation({
            query: (data) => ({
                url: 'general/logout',
                method: 'POST',
                body: data,
            }),
            providesTags: ['Logout'],
        }),
        updateUser: build.mutation({
            query: (userData) => ({
                url: 'general/update',
                method: 'POST',
                body: userData,
            }),
            providesTags: ['UpdateUser'],
        }),
        changePassword: build.mutation({
            query: ({ userId, oldPassword, newPassword }) => ({
                url: `general/changePassword/${userId}`,
                method: 'POST',
                body: { oldPassword, newPassword },
            }),
            providesTags: ['ChangePassword'],
        }),

        //user
        getUser: build.query({
            query: (userId) => `user/user/${userId}`,
            providesTags: ['User'],
        }),
        getAllUsers: build.query({
            query: (company) => `user/getAllUsers/${company}`,
            providesTags: ['AllUser'],
        }),
        sendInvitation: build.mutation({
            query: ({ name, email }) => ({
                url: `user/sendInvitation`,
                method: 'POST',
                body: { name, email },
            }),
            providesTags: ['SendInvitation'],
        }),
        addUser: build.mutation({
            query: ({ name, email, company, role }) => ({
                url: `user/addUser`,
                method: 'POST',
                body: { name, email, company, role },
            }),
            providesTags: ['AddUser'],
        }),
        editUser: build.mutation({
            query: ({ userId, data }) => ({
                url: `user/editUser/${userId}`,
                method: 'POST',
                body: data,
            }),
            providesTags: ['EditUser'],
        }),
        activateUser: build.mutation({
            query: (userId) => ({
                url: `user/activateUser/${userId}`,
                method: 'POST',
            }),
            providesTags: ['ActivateUser'],
        }),
        deactivateUser: build.mutation({
            query: (userId) => ({
                url: `user/deactivateUser/${userId}`,
                method: 'POST',
            }),
            providesTags: ['DeactivateUser'],
        }),
        removeUser: build.mutation({
            query: (userId) => ({
                url: `user/removeUser/${userId}`,
                method: 'POST',
            }),
            providesTags: ['RemoveUser'],
        }),

        //devices
        getDevices: build.query({
            query: (company) => `device/devices/${company}`,
            providesTags: ['Devices'],
        }),
        getDeviceNames: build.query({
            query: (company) => `device/deviceNames/${company}`,
            providesTags: ['Devices'],
        }),
        addDevice: build.mutation({
            query: (data) => ({
                url: 'device/addDevice',
                method: 'POST',
                body: data,
            }),
            providesTags: ['AddDevice'],
        }),
        removeDevice: build.mutation({
            query: (data) => ({
                url: 'device/removeDevice',
                method: 'POST',
                body: data,
            }),
            providesTags: ['removeDevice'],
        }),
        downtime: build.query({
            query: (mac) => `device/downtime/${mac}`,
            provideTags: ['Downtime'],
        }),

        //sites
        getSites: build.query({
            query: (company) => `site/site/${company}`,
            providesTags: ['Sites'],
        }),
        getAllSites: build.query({
            query: () => `site/allSite/`,
            providesTags: ['AllSites'],
        }),
        addSite: build.mutation({
            query: (data) => ({
                url: 'site/addSite',
                method: 'POST',
                body: data,
            }),
            providesTags: ['AddSite'],
        }),
        removeSite: build.mutation({
            query: (siteId) => ({
                url: `site/removeSite/${siteId}`,
                method: 'POST',
            }),
            providesTags: ['RemoveSite'],
        }),
        disableSite: build.mutation({
            query: (siteId) => ({
                url: `site/disableSite/${siteId}`,
                method: 'POST',
            }),
            providesTags: ['DisableSite'],
        }),
        enableSite: build.mutation({
            query: (siteId) => ({
                url: `site/enableSite/${siteId}`,
                method: 'POST',
            }),
            providesTags: ['EnableSite'],
        }),
        updateSite: build.mutation({
            query: ({ siteId, data }) => ({
                url: `site/update-site/${siteId}`,
                method: 'PUT',
                body: data,
            }),
            providesTags: ['UpdateSite'],
        }),

        //logics
        getLogics: build.query({
            query: () => 'logic/logic',
            providesTags: ['Logics'],
        }),

        //data
        totalData: build.query({
            query: (groupIds) => ({
                url: `data/totalData`,
                method: 'POST',
                body: {
                    groupIds,
                },
            }),
            providesTags: ['TotalData'],
        }),
        overviewData: build.query({
            query: ({ mac }) => `data/overviewData/${mac}`,
            providesTags: ['OverviewData'],
        }),
        hourlyData: build.query({
            query: ({ mac, startDate, endDate }) => ({
                url: `data/hourlyData`,
                method: 'POST',
                body: { mac, startDate, endDate },
            }),
            providesTags: ['HourlyData'],
        }),
        dailyData: build.query({
            query: ({ mac }) => `data/dailyData/${mac}`,
            providesTags: ['DailyData'],
        }),
        weeklyData: build.query({
            query: ({ mac }) => `data/weeklyData/${mac}`,
            providesTags: ['WeeklyData'],
        }),
        monthlyData: build.query({
            query: ({ mac }) => `data/monthlyData/${mac}`,
            providesTags: ['MonthlyData'],
        }),
        yearlyData: build.query({
            query: ({ mac }) => `data/yearlyData/${mac}`,
            providesTags: ['YearlyData'],
        }),

        //adult&childdata
        adultnChildOverviewData: build.query({
            query: ({ mac }) => `data/adult&ChildOverviewData/${mac}`,
            providesTags: ['AdultnChildOverviewData'],
        }),
        adultnChildHourlyData: build.query({
            query: ({ mac, startDate, endDate }) => ({
                url: `data/adult&ChildHourlyData`,
                method: 'POST',
                body: { mac, startDate, endDate },
            }),
            providesTags: ['AdultnChildHourlyData'],
        }),
        adultnChildDailyData: build.query({
            query: ({ mac }) => `data/adult&ChildDailyData/${mac}`,
            providesTags: ['AdultnChildDailyData'],
        }),
        adultnChildWeeklyData: build.query({
            query: ({ mac }) => `data/adult&ChildWeeklyData/${mac}`,
            providesTags: ['AdultnChildWeeklyData'],
        }),
        adultnChildMonthlyData: build.query({
            query: ({ mac }) => `data/adult&ChildMonthlyData/${mac}`,
            providesTags: ['AdultnChildMonthlyData'],
        }),
        adultnChildYearlyData: build.query({
            query: ({ mac }) => `data/adult&ChildYearlyData/${mac}`,
            providesTags: ['AdultnChildYearlyData'],
        }),

        //gender
        genderOverviewData: build.query({
            query: ({ mac }) => `data/genderOverviewData/${mac}`,
            providesTags: ['GenderOverviewData'],
        }),
        genderHourlyData: build.query({
            query: ({ mac, startDate, endDate }) => ({
                url: `data/genderHourlyData`,
                method: 'POST',
                body: { mac, startDate, endDate },
            }),
            providesTags: ['GenderHourlyData'],
        }),
        genderDailyData: build.query({
            query: ({ mac }) => `data/genderDailyData/${mac}`,
            providesTags: ['GenderDailyData'],
        }),
        genderWeeklyData: build.query({
            query: ({ mac }) => `data/genderWeeklyData/${mac}`,
            providesTags: ['GenderWeeklyData'],
        }),
        genderMonthlyData: build.query({
            query: ({ mac }) => `data/genderMonthlyData/${mac}`,
            providesTags: ['GenderMonthlyData'],
        }),
        genderYearlyData: build.query({
            query: ({ mac }) => `data/genderYearlyData/${mac}`,
            providesTags: ['GenderYearlyData'],
        }),

        //notifications
        getNotifications: build.query({
            query: () => 'device/notifications',
            providesTags: ['Notifications'],
        }),

        //reports
        extremeData: build.query({
            query: ({ mac, month, year }) => ({
                url: `report/extremeData`,
                method: 'POST',
                body: { mac, month, year },
            }),
            providesTags: ['ExtremeData'],
        }),
        monthlyReportData: build.query({
            query: ({ mac, month, year }) => ({
                url: `report/monthlyReportData`,
                method: 'POST',
                body: { mac, month, year },
            }),
            providesTags: ['MonthlyReportData'],
        }),
        adultnChildExtremeData: build.query({
            query: ({ mac, month, year }) => ({
                url: `report/adult&ChildExtremeData`,
                method: 'POST',
                body: { mac, month, year },
            }),
            providesTags: ['AdultnChildExtremeData'],
        }),
        adultnChildMonthlyReportData: build.query({
            query: ({ mac, month, year }) => ({
                url: `report/adult&ChildMonthlyReportData`,
                method: 'POST',
                body: { mac, month, year },
            }),
            providesTags: ['AdultnChildMonthlyReportData'],
        }),
        updateSheet: build.mutation({
            query: (data) => ({
                url: 'report/updateSheet',
                method: 'POST',
                body: data,
            }),
            providesTags: ['UpdateSheet'],
        }),
        adultnChildUpdateSheet: build.mutation({
            query: (data) => ({
                url: 'report/adult&ChildUpdateSheet',
                method: 'POST',
                body: data,
            }),
            providesTags: ['AdultnChildUpdateSheet'],
        }),
        sendReportByEmail: build.mutation({
            query: (data) => ({
                url: 'report/sendEmail',
                method: 'POST',
                body: data,
            }),
            providesTags: ['SendReportEmail'],
        }),

        //companies
        getCompanies: build.query({
            query: () => 'company/companies',
            providesTags: ['Companies'],
        }),
        getTotalCompanies: build.query({
            query: () => 'company/companyCounts',
            providesTags: ['TotalCompanies'],
        }),
        addCompany: build.mutation({
            query: (data) => ({
                url: 'company/addCompany',
                method: 'POST',
                body: data,
            }),
            providesTags: ['AddCompany'],
        }),
        updateCompany: build.mutation({
            query: ({ companyName, data }) => ({
                url: `company/updateCompany/${companyName}`,
                method: 'POST',
                body: data,
            }),
            providesTags: ['UpdateCompany'],
        }),
        getCompany: build.query({
            query: (company) => `company/company/${company}`,
            providesTags: ['Company'],
        }),
        assignCompanyToPowerAdmin: build.mutation({
            query: ({ userId, companyId }) => ({
                url: `company/assignCompanyToPowerAdmin`,
                method: 'POST',
                body: { userId, companyId },
            }),
            providesTags: ['AssignCompany'],
        }),
        removeCompanyFromPowerAdmin: build.mutation({
            query: ({ userId, companyId }) => ({
                url: `company/removeCompanyFromPowerAdmin`,
                method: 'POST',
                body: { userId, companyId },
            }),
            providesTags: ['RemoveCompany'],
        }),
        updateUserLimit: build.mutation({
            query: ({ companyId, userLimit }) => ({
                url: `company/update-limit/${companyId}`,
                method: 'PUT',
                body: { userLimit },
            }),
        }),

        //alternatives
        getAlternatives: build.query({
            query: (company) => `alternative/getAlternative/${company}`,
            providesTags: ['Alternatives'],
        }),
        updateAlternative: build.mutation({
            query: (data) => ({
                url: 'alternative/updateAlternative',
                method: 'POST',
                body: data,
            }),
            providesTags: ['UpdateAlternative'],
        }),
        updateSiteAlternative: build.mutation({
            query: (data) => ({
                url: 'alternative/updateSiteAlternative',
                method: 'POST',
                body: data,
            }),
            providesTags: ['UpdateSiteAlternative'],
        }),

        //permissions
        getPermissions: build.query({
            query: () => 'permission/permissions',
            provideTags: ['Permisions'],
        }),

        //roles
        addPermissionToUser: build.mutation({
            query: ({ userId, permissionId }) => ({
                url: `role/addPermission`,
                method: 'POST',
                body: { userId, permissionId },
            }),
            providesTags: ['AddPermissionToUser'],
        }),
        removePermissionFromUser: build.mutation({
            query: ({ userId, permissionId }) => ({
                url: `role/removePermission`,
                method: 'POST',
                body: { userId, permissionId },
            }),
            providesTags: ['RemovePermissionFromUser'],
        }),

        //licenses
        getLicenses: build.query({
            query: (company) => `license/licenses/${company}`,
            providesTags: ['Licenses'],
        }),
        addLicenses: build.mutation({
            query: ({ deviceId, companyId, packageId }) => ({
                url: `license/addLicenses`,
                method: 'POST',
                body: { deviceId, companyId, packageId },
            }),
            providesTags: ['AddLicenses'],
        }),

        //packages
        getPackages: build.query({
            query: () => 'package/packages',
            providesTags: ['Packages'],
        }),

        addPackage: build.mutation({
            query: ({ name, description, durationInDays, startDate, endDate }) => ({
                url: `package/addPackage`,
                method: 'POST',
                body: { name, description, durationInDays, startDate, endDate },
            }),
            providesTags: ['AddPackage'],
        }),

        //addon
        getAddons: build.query({
            query: (company) => `addon/getAddons/${company}`,
            providesTags: ['Addon'],
        }),
        getAddonByDevice: build.query({
            query: (mac) => `addon/getAddonsByDevice/${mac}`,
            providesTags: ['Addon'],
        }),
        updateAddon: build.mutation({
            query: ({ addonId, data }) => ({
                url: `addon/updateAddon/${addonId}`,
                method: 'PUT',
                body: data,
            }),
            providesTags: ['UpdateAddon'],
        }),

        //group
        getGroups: build.query({
            query: (company) => `group/${company}`,
            providesTags: ['Group'],
        }),
        addGroup: build.mutation({
            query: ({ name, sites, devices, users, company }) => ({
                url: `group/create-group`,
                method: 'POST',
                body: { name, sites, devices, users, company },
            }),
            providesTags: ['AddGroup'],
        }),
        updateGroup: build.mutation({
            query: ({ id, name, sites, devices, users, company }) => ({
                url: `group/${id}`,
                method: 'PUT',
                body: { name, sites, devices, users, company },
            }),
            providesTags: ['UpdateGroup'],
        }),
        deleteGroup: build.mutation({
            query: (id) => ({
                url: `group/${id}`,
                method: 'DELETE',
            }),
            providesTags: ['DeleteGroup'],
        }),

        //Global
        getGlobals: build.query({
            query: (companyId) => `global/${companyId}`,
            providesTags: ['Global'],
        }),
        flipDowntime: build.mutation({
            query: (companyId) => ({
                url: `global/flip-downtime/${companyId}`,
                method: 'PATCH',
            }),
            providesTags: ['FlipDowntime'],
        }),
    }),
});

export const {
    //general
    useLoginMutation,
    useSignupMutation,
    useGetUserQuery,
    useGetAllUsersQuery,
    useLogoutMutation,
    useUpdateUserMutation,
    useAddUserMutation,
    useActivateUserMutation,
    useDeactivateUserMutation,
    useRemoveUserMutation,
    useEditUserMutation,
    useChangePasswordMutation,
    useSendInvitationMutation,

    //devices
    useGetDevicesQuery,
    useGetDeviceNamesQuery,
    useAddDeviceMutation,
    useRemoveDeviceMutation,
    useDowntimeQuery,

    //sites
    useGetSitesQuery,
    useGetAllSitesQuery,
    useAddSiteMutation,
    useRemoveSiteMutation,
    useDisableSiteMutation,
    useEnableSiteMutation,
    useUpdateSiteMutation,

    //logics
    useGetLogicsQuery,

    //data
    useOverviewDataQuery,
    useHourlyDataQuery,
    useDailyDataQuery,
    useWeeklyDataQuery,
    useMonthlyDataQuery,
    useYearlyDataQuery,
    useTotalDataQuery,

    //adult&childdata
    useAdultnChildOverviewDataQuery,
    useAdultnChildHourlyDataQuery,
    useAdultnChildDailyDataQuery,
    useAdultnChildWeeklyDataQuery,
    useAdultnChildMonthlyDataQuery,
    useAdultnChildYearlyDataQuery,

    //genderData
    useGenderOverviewDataQuery,
    useGenderHourlyDataQuery,
    useGenderDailyDataQuery,
    useGenderWeeklyDataQuery,
    useGenderMonthlyDataQuery,
    useGenderYearlyDataQuery,

    //notifications
    useGetNotificationsQuery,

    //reports
    useExtremeDataQuery,
    useMonthlyReportDataQuery,
    useAdultnChildExtremeDataQuery,
    useAdultnChildMonthlyReportDataQuery,
    useUpdateSheetMutation,
    useAdultnChildUpdateSheetMutation,
    useSendReportByEmailMutation,

    //companies
    useGetCompaniesQuery,
    useGetTotalCompaniesQuery,
    useAddCompanyMutation,
    useUpdateCompanyMutation,
    useGetCompanyQuery,
    useAssignCompanyToPowerAdminMutation,
    useRemoveCompanyFromPowerAdminMutation,
    useUpdateUserLimitMutation,

    //alternatives
    useGetAlternativesQuery,
    useUpdateAlternativeMutation,
    useUpdateSiteAlternativeMutation,

    //permissions
    useGetPermissionsQuery,

    //roles
    useAddPermissionToUserMutation,
    useRemovePermissionFromUserMutation,

    //licenses
    useGetLicensesQuery,
    useAddLicensesMutation,

    //packages
    useGetPackagesQuery,
    useAddPackageMutation,

    //addon
    useGetAddonsQuery,
    useGetAddonByDeviceQuery,
    useUpdateAddonMutation,

    //group
    useGetGroupsQuery,
    useAddGroupMutation,
    useDeleteGroupMutation,
    useUpdateGroupMutation,

    //global
    useGetGlobalsQuery,
    useFlipDowntimeMutation,
} = api;
